import React from "react";
import cns from "classnames";
import { useTenant } from "@hooks";
import { useObserver } from "mobx-react";
import { Grid } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Select, Text } from "@components/formx";
import {
  InitialContact,
  PreferredCommunication,
  SelfRegisterOption,
  Tenant
} from "@bonliva-crm/shared";
import { Body } from "@components";
import BasicSelect from "@components/Select";
import ConsultantSelector from "@containers/VirtualSelector/ConsultantSelector";
import { InputLookupAdornment } from "@components/InputLookupAdornment";
import { useConsultantEditorContext } from "../ConsultantEditorContext";
import { ConsultantEditorStore } from "../ConsultantEditorStore";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import { sortBy } from "lodash";

export function ContactsInfo() {
  const editorStore: ConsultantEditorStore = useConsultantEditorContext();
  const classes = useStyles({});
  const { tenant } = useTenant();

  return useObserver(() => {
    let isInitialContactDisabled = false;

    if (
      !editorStore.isNew &&
      editorStore.initialContact &&
      !editorStore.canUpdateInitialContact
    ) {
      isInitialContactDisabled = true;
    }

    const isCellPhoneLookupDisabled =
      !editorStore.cellPhone ||
      !editorStore.isValid("contacts.cellPhone") ||
      editorStore.isLookingUp;
    const isPhoneLookupDisabled =
      !editorStore.phone ||
      !editorStore.isValid("contacts.phone") ||
      editorStore.isLookingUp;
    const isSelfRegisterOptionSelected =
      editorStore.initialContact === InitialContact.SelfRegister;

    const initialContactOptionsMap = {
      [Tenant.WCC]: [
        InitialContact.CreatedByWCC,
        InitialContact.SelfRegister,
        InitialContact.Recommendation
      ],
      [Tenant.BonlivaAB]: [
        InitialContact.CreatedByBonliva,
        InitialContact.SelfRegister,
        InitialContact.Recommendation
      ]
    };

    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Text
            label={locale.Email}
            name={"contacts.email"}
            TextFieldProps={{
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("contacts.email")
              })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Text
            label={locale.consultantSecondaryEmail}
            name={"contacts.secondaryEmail"}
            TextFieldProps={{
              className: cns({
                [classes.notApproved]: editorStore.consultantCVManager.keyNotApproved(
                  "contacts.secondaryEmail"
                )
              })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Text
            name={"contacts.cellPhone"}
            label={locale["CellPhone number"]}
            TextFieldProps={{
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("contacts.cellPhone")
              }),
              InputProps: {
                endAdornment: (
                  <InputLookupAdornment
                    disabled={isCellPhoneLookupDisabled}
                    loading={editorStore.isLookingUp}
                    onLookup={() =>
                      editorStore.phoneLookup("Sweden", editorStore.cellPhone)
                    }
                  />
                )
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Text
            name={"contacts.phone"}
            label={locale["Phone number"]}
            TextFieldProps={{
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("contacts.phone")
              }),
              InputProps: {
                endAdornment: (
                  <InputLookupAdornment
                    disabled={isPhoneLookupDisabled}
                    loading={editorStore.isLookingUp}
                    onLookup={() => editorStore.phoneLookup("Sweden", editorStore.phone)}
                  />
                )
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            multi
            name={"contacts.preferredCommunication"}
            label={locale["Preferable communication"]}
            options={Object.keys(PreferredCommunication).toLabelValue(
              (c) => locale.preferredCommunication[c.toLowerCase()]
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <BasicSelect
            name={"contacts.initialContact"}
            disabled={isInitialContactDisabled}
            required
            fullWidth
            label={locale.InitialContact}
            options={(initialContactOptionsMap[tenant] || []).toLabelValue(
              (c) => locale.initialContact[c]
            )}
            onChange={editorStore.updateInitialContact}
            value={editorStore.initialContact}
            error={!!editorStore.form.getError("contacts.initialContact")}
          />
        </Grid>
        {isSelfRegisterOptionSelected && (
          <Grid item xs={12} sm={6}>
            <BasicSelect
              name={"contacts.selfRegisterOption"}
              resetValue={null}
              fullWidth
              disabled={isInitialContactDisabled}
              label={locale.fillSelfRegisterOption}
              options={sortBy(
                Object.keys(SelfRegisterOption)
                  .filter((v) => v !== SelfRegisterOption.SelfRegisterNotSpecified)
                  .toLabelValue((c) => locale.selfRegisterOption[c]),
                "label"
              )}
              onChange={editorStore.updateSelfRegisterOption}
              value={editorStore.selfRegisterOption}
            />
          </Grid>
        )}
        {editorStore.initialContact === InitialContact.Recommendation &&
          !isInitialContactDisabled && (
            <Grid item xs={12} sm={6} style={{ zIndex: 2 }}>
              <ConsultantSelector
                fullWidth
                slim
                error={editorStore.form.getError("contacts.recommendedByConsultant")}
                onChange={editorStore.updateRecommendedByConsultant}
                value={editorStore.recommendedByConsultantValue}
              />
            </Grid>
          )}
        {editorStore.initialContact === InitialContact.Recommendation &&
          isInitialContactDisabled && (
            <Grid item xs={12} sm={6} className={classes.readOnlyRecommendedBy}>
              <Body>{locale.RecommendedBy}:</Body>
              <Body>{editorStore.recommendedBy}</Body>
            </Grid>
          )}
      </Grid>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  notApproved: {
    ...theme.palette.error
  },
  readOnlyRecommendedBy: {
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "column"
  }
}));
